/**
 * useEditSession — TanStack Query wrapper around
 * ``POST /api/v1/documents/{doc_id}/edit-session``.
 *
 * Returns a presigned RW window (``read_url`` GET 3600s + ``save_url`` PUT
 * 1800s) the inline editors (PDF / DOCX / XLSX) consume. The hook is the
 * SOTA replacement for the old "find the doc in the paginated list" path
 * that broke when a chat citation pointed at a doc outside the first page
 * (May 21, 2026 user-visible bug: "Documento non trovato" overlay).
 *
 * Caching rules:
 *   - ``queryKey = ["edit-session", docId]`` — one cache entry per doc.
 *   - ``staleTime = 25 min`` — the BE issues a 30-min ``save_url``; we keep
 *     the cached session reusable until 5 min before expiry so the editor
 *     never serves a presigned URL that's about to die mid-PUT.
 *   - ``gcTime = 35 min`` — slightly past the save_url TTL so a
 *     remount-after-unmount within the same workspace session reuses the
 *     URL when still valid.
 *   - ``retry: 1`` — 404 means the doc was deleted server-side or the
 *     citation refers to a cross-tenant artefact; retrying is wasted load.
 *
 * On 412 Precondition Failed during save the editor invalidates this
 * cache entry to force a fresh session-open.
 */

import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { openEditSession, type EditSession } from "@ando/core";
import type { EditSession as UIEditSession } from "@ando/ui/editors";

// Compile-time guarantee that the FE-side ``EditSession`` mirrored by the
// leaf ``@ando/ui`` package stays in sync with the canonical core wire type.
// ``@ando/ui`` must stay zero-dep on ``@ando/core`` (leaf rule, see
// ``packages/ui/src/ando/sources/types.ts``); this assertion makes the
// duplication SAFE rather than fragile — any drift trips ``tsc --noEmit``.
type _EditSessionMirrorCheck = [
  UIEditSession extends EditSession ? true : false,
  EditSession extends UIEditSession ? true : false,
] extends [true, true]
  ? true
  : never;
const _editSessionMirrorOk: _EditSessionMirrorCheck = true;
void _editSessionMirrorOk;

export function editSessionQueryKey(docId: string): readonly unknown[] {
  return ["edit-session", docId] as const;
}

// BE save_url TTL is 7200s = 120min (``MINIO_PRESIGN_WRITE_TTL_SECONDS``
// in ``ando-agent/src/config.py``). We keep the cached session reusable
// until 10min before expiry so the editor never serves a presigned URL
// that's about to die mid-PUT, and gc 5min past TTL so a remount within
// the same workspace session reuses a still-valid URL.
const STALE_MS = 110 * 60_000;
const GC_MS = 125 * 60_000;

export function useEditSession(
  docId: string | null | undefined,
): UseQueryResult<EditSession> {
  return useQuery<EditSession>({
    queryKey: editSessionQueryKey(docId ?? ""),
    queryFn: () => openEditSession(docId ?? ""),
    enabled: typeof docId === "string" && docId.length > 0,
    staleTime: STALE_MS,
    gcTime: GC_MS,
    retry: 1,
  });
}
